import './xeroPurchaseOrderFieldsSection.scss';

import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { Text, Tooltip } from '@approvalmax/ui/src/components';
import { stringHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useGetRecognizedOcrAttachment } from 'pages/requestForm/hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import {
    getCanCreateContact,
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
    RequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import { FC, memo, useMemo, useRef } from 'react';
import bemFactory from 'react-bem-factory';
import { ocrIsLoadingState } from 'shared/states';

import { changeXeroSupplier, editRequestField, showXeroContactCreationPopup } from '../../../actions';
import { XeroContact } from '../../../data/xero/XeroContact';
import { getShowValidationErrors } from '../../../selectors/pageSelectors';
import { getXeroContext } from '../../../selectors/xero';
import { SupplierDataProvider } from '../dataProviders/SupplierDataProvider';
import { XeroContactDetails } from '../XeroContactDetails/XeroContactDetails';
import { messages } from './XeroPurchaseOrderFieldsSection.messages';
import { XeroPurchaseOrderFieldsSectionProps } from './XeroPurchaseOrderFieldsSection.types';

const { xeroConstants, commonConstants } = constants;

const bem = bemFactory.block('reqf-xero-purchase-order-fields-section');
const qa = bemFactory.qa('reqf-xero-purchase-order-fields-section');

export const XeroPurchaseOrderFieldsSection: FC<XeroPurchaseOrderFieldsSectionProps> = memo((props) => {
    const { className, request } = props;

    const {
        details: { contact, reference, date, deliveryDate, brandingTheme },
    } = request;
    const xeroContext = useSelector(getXeroContext);
    const showValidationErrors = useSelector(getShowValidationErrors);
    const dispatch = useDispatch();
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;
    const initialBrandingTheme = useRef(brandingTheme);
    const initialContact = useRef(contact);
    const [
        supplierFieldPermission,
        dateFieldPermission,
        deliveryDateFieldPermission,
        brandingThemeFieldPermission,
        referenceFieldPermission,
    ] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [
            domain.FieldSystemPurpose.XeroSupplier,
            domain.FieldSystemPurpose.XeroDate,
            domain.FieldSystemPurpose.XeroDeliveryDate,
            domain.FieldSystemPurpose.XeroBranding,
            domain.FieldSystemPurpose.XeroReference,
        ])
    );

    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));

    const isReferenceInvalid =
        (reference?.length ?? 0) > xeroConstants.PO_REFERENCE_MAX_LENGTH ||
        (referenceFieldPermission.required && !reference);
    const supplierStaticValues = useMemo(
        () => (isApproverMode && initialContact.current ? [initialContact.current] : []),
        [isApproverMode]
    );
    const brandingThemeOptions = useMemo(
        () =>
            isApproverMode && initialBrandingTheme.current
                ? [initialBrandingTheme.current, ...xeroContext.brandingThemes]
                : xeroContext.brandingThemes,
        [isApproverMode, xeroContext.brandingThemes]
    );

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const showContactOcrWarning = Boolean(ocrData?.document?.contactIds && ocrData.document.contactIds.length > 1);
    const showContactMissingWarning = showValidationErrors && !contact;

    const createContact = (text: string | null) => {
        dispatch(showXeroContactCreationPopup(text));

        return Promise.resolve(null);
    };

    const onChangeXeroSupplier = (supplier: XeroContact | null) => {
        dispatch(changeXeroSupplier(supplier));
    };

    const handleChangeDate = (date: string | null) => {
        dispatch(editRequestField<domain.XeroPurchaseOrderDetails>('date', date));
    };

    const handleChangeDeliveryDate = (date: string | null) => {
        dispatch(editRequestField<domain.XeroPurchaseOrderDetails>('deliveryDate', date));
    };

    const handleChangeReference = (value: string) => {
        if (!commonConstants.ONLY_SPACES_REG_EXP.test(value)) {
            dispatch(
                editRequestField<domain.XeroPurchaseOrderDetails>(
                    'reference',
                    stringHelpers.escapeSpecificExpressions(value, xeroConstants.XERO_NOT_ALLOWED_SYMBOLS)
                )
            );
        }
    };

    const handleChangeBrandingTheme = (value: Reference | null) => {
        dispatch(editRequestField<domain.XeroPurchaseOrderDetails>('brandingTheme', value));
    };

    return (
        <div className={bem.add(className)()}>
            <Field className={bem('field-item', 'contact')} required title={messages.contactFieldTitle}>
                <Tooltip
                    disabled={!showContactOcrWarning}
                    placement='top'
                    width='max-content'
                    activator={
                        <SupplierDataProvider staticValues={supplierStaticValues}>
                            <DropdownEditor
                                disabled={ocrIsLoading || supplierFieldPermission.disabled}
                                warning={showContactOcrWarning && !showContactMissingWarning}
                                invalid={showContactMissingWarning}
                                qa={qa('supplier-editor')}
                                value={contact}
                                singleLinePerItem={false}
                                onChange={onChangeXeroSupplier}
                                placeholder={messages.contactFieldPlaceholder}
                                canCreateItemFn={() => canCreateContact}
                                createItemDisplayTextFn={() => messages.addContactButton}
                                onCreateItem={createContact}
                            />
                        </SupplierDataProvider>
                    }
                >
                    <Text spacing='8' font='body' fontSize='small'>
                        {messages.multipleContactsWarning}
                    </Text>
                </Tooltip>

                <XeroContactDetails contact={contact} />
            </Field>

            <Field className={bem('field-item')} required title={messages.dateFieldTitle}>
                <DateTimeEditor
                    qa={qa('date-editor')}
                    invalid={showValidationErrors && !date}
                    value={date}
                    onChange={handleChangeDate}
                    placeholder={messages.dateFieldPlaceholder}
                    disabled={dateFieldPermission.disabled}
                />
            </Field>

            <Field
                className={bem('field-item')}
                title={messages.deliveryDateFieldTitle}
                required={deliveryDateFieldPermission.required}
            >
                <DateTimeEditor
                    qa={qa('delivery-date-editor')}
                    value={deliveryDate}
                    onChange={handleChangeDeliveryDate}
                    placeholder={messages.deliveryDateFieldPlaceholder}
                    disabled={deliveryDateFieldPermission.disabled}
                    invalid={deliveryDateFieldPermission.required && showValidationErrors && !deliveryDate}
                />
            </Field>

            <Field
                className={bem('field-item')}
                title={messages.referenceFieldTitle}
                required={referenceFieldPermission.required}
            >
                <TextEditor
                    qa={qa('reference-editor')}
                    invalid={showValidationErrors && isReferenceInvalid}
                    value={reference ?? ''}
                    maxLength={xeroConstants.PO_REFERENCE_MAX_LENGTH}
                    onChange={handleChangeReference}
                    placeholder={messages.referenceFieldPlaceholder}
                    disabled={referenceFieldPermission.disabled}
                />
            </Field>

            <Field className={bem('field-item')} title={messages.themeFieldTitle}>
                <PlainDataProvider items={brandingThemeOptions}>
                    <DropdownEditor
                        qa={qa('branding-editor')}
                        invalid={showValidationErrors && !brandingTheme}
                        value={brandingTheme}
                        onChange={handleChangeBrandingTheme}
                        placeholder={messages.themeFieldPlaceholder}
                        buttonComponent={DropdownEditor.NoFilterButton}
                        disabled={brandingThemeFieldPermission.disabled}
                    />
                </PlainDataProvider>
            </Field>
        </div>
    );
});

XeroPurchaseOrderFieldsSection.displayName = 'XeroPurchaseOrderFieldsSection';
